import * as React from 'react'

import { Tag, TagColor } from '@factorialco/deprecated-design-system'

import { observer } from 'mobx-react'

import CheckBox from 'modules/core/components/Inputs/CheckBox'
import { TableHead } from 'modules/core/components/Table'
import { TableBody } from 'modules/core/components/Table'
import { TableRow } from 'modules/core/components/Table'
import { TableHeadCell } from 'modules/core/components/Table'
import { TableBodyCell } from 'modules/core/components/Table'
import type { WithMultiSelectStateChildrenProps } from 'modules/core/components/WithMultiSelectState'
import i18n from 'modules/core/lib/i18n'

import type { ImportedEmployee } from 'modules/employees/lib/ImportedEmployee'

import getAttributeHeader from 'modules/employees/pages/ImportEmployeesPage/utils/getAttributeHeader'
import BasicCellContent from './BasicCellContent'
import CustomFieldCellContent from './CustomFieldCellContent'

type WithMultiSelectProps = WithMultiSelectStateChildrenProps<ImportedEmployee>

interface Props {
  employees: ImportedEmployee[]
  selected: ImportedEmployee[]
  attributes: string[]
  customFields: string[]
  onToggleSelectedItem: WithMultiSelectProps['onToggleSelectedItem']
  onDeselectAll: () => void
  onSelectAll: () => void
}

const Content = ({
  employees,
  selected,
  attributes,
  customFields,
  onToggleSelectedItem,
  onDeselectAll,
  onSelectAll,
}: Props) => {
  const onToggleItem = (employee: ImportedEmployee) => {
    if (employee.status !== 'errored') {
      onToggleSelectedItem(employee)
    }
  }

  const onClickAll = () => {
    if (employees.length === selected.length) {
      onDeselectAll()
    } else {
      onSelectAll()
    }
  }

  return (
    <>
      <TableHead>
        <TableRow>
          <TableHeadCell key="check">
            <CheckBox
              checked={selected.length === employees.length}
              disabled={employees.some((employee) => employee.status === 'errored')}
              onClick={onClickAll}
            />
          </TableHeadCell>
          <TableHeadCell key="type">{i18n.t('import.table.headers.type')}</TableHeadCell>

          {attributes.map((attribute) => (
            <TableHeadCell key={attribute}>{getAttributeHeader(attribute)}</TableHeadCell>
          ))}
          {customFields.map((attribute: string, index: number) => (
            <TableHeadCell key={index}>{attribute}</TableHeadCell>
          ))}
        </TableRow>
      </TableHead>
      <TableBody>
        {employees.map((employee) => {
          const checked = selected.includes(employee)

          const type: React.ComponentProps<typeof Tag>['type'] =
            employee.type === 'new' ? 'brand' : 'grey5'

          const extraProps: { color?: TagColor } =
            employee.status === 'errored'
              ? {
                  color: TagColor.wrong,
                }
              : {}

          return (
            <TableRow key={employee.email} {...extraProps}>
              <TableBodyCell key="check" centered>
                <CheckBox
                  checked={checked}
                  disabled={employee.status === 'errored'}
                  onClick={() => onToggleItem(employee)}
                />
              </TableBodyCell>
              <TableBodyCell key="type" centered>
                <Tag upcased size="small" type={type}>
                  {employee.type}
                </Tag>
              </TableBodyCell>

              {attributes.map((attribute) => (
                <TableBodyCell key={attribute}>
                  <BasicCellContent employee={employee} attribute={attribute} />
                </TableBodyCell>
              ))}

              {customFields.map((_attribute: string, index: number) => (
                <TableBodyCell key={index}>
                  <CustomFieldCellContent employee={employee} index={index} />
                </TableBodyCell>
              ))}
            </TableRow>
          )
        })}
      </TableBody>
    </>
  )
}

export default observer(Content)
